import * as yup from "yup";
import { TFunction } from "i18next";

export const createProposalValidationSchema = (t: TFunction) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return yup.object().shape({
    vehicleData: yup.object().shape({
      licensePlateNumber: yup
        .string()
        .required(t("qtg.proposal.validation.licensePlateRequired")),
      vin: yup
        .string()
        .min(17, t("qtg.proposal.validation.vinLength"))
        .max(17, t("qtg.proposal.validation.vinLength")),
      vehicleMake: yup
        .string()
        .required(t("qtg.proposal.validation.vehicleMakeRequired")),
      vehicleModel: yup
        .string()
        .required(t("qtg.proposal.validation.vehicleModelRequired")),
      productionYear: yup
        .number()
        .min(1900, t("qtg.proposal.validation.productionYearMin"))
        .max(
          new Date().getFullYear(),
          t("qtg.proposal.validation.productionYearMax")
        )
        .test(
          "is-past",
          t("qtg.proposal.validation.productionYearPast"),
          (value) => {
            if (!value) return true;
            return value <= new Date().getFullYear();
          }
        ),
      // Priority 1: Numeric fields must be > 0
      engineCapacity: yup
        .number()
        .transform((value, originalValue) => {
          return originalValue === "" ? undefined : value;
        })
        .positive(t("qtg.proposal.validation.engineCapacityPositive"))
        .moreThan(0, t("qtg.proposal.validation.engineCapacityRequired")),
      power: yup
        .number()
        .transform((value, originalValue) => {
          return originalValue === "" ? undefined : value;
        })
        .positive(t("qtg.proposal.validation.powerPositive"))
        .moreThan(0, t("qtg.proposal.validation.powerRequired")),
      weight: yup
        .number()
        .transform((value, originalValue) => {
          return originalValue === "" ? undefined : value;
        })
        .positive(t("qtg.proposal.validation.weightPositive"))
        .moreThan(0, t("qtg.proposal.validation.weightRequired")),
      // Owner Email and Owner Phone are required only when vehicle is leased
      ownerEmail: yup.string().when("isLeased", {
        is: true,
        then: (schema) =>
          schema
            .required(t("qtg.proposal.validation.ownerEmailRequired"))
            .email(t("qtg.proposal.validation.ownerEmailInvalid")),
        otherwise: (schema) =>
          schema.email(t("qtg.proposal.validation.ownerEmailInvalid")),
      }),
      ownerPhone: yup.string().when("isLeased", {
        is: true,
        then: (schema) =>
          schema.required(t("qtg.proposal.validation.ownerPhoneRequired")),
      }),
    }),
    clientData: yup.object().shape({
      firstName: yup
        .string()
        .required(t("qtg.proposal.validation.firstNameRequired")),
      lastName: yup
        .string()
        .required(t("qtg.proposal.validation.lastNameRequired")),
      // Priority 1: Either email OR phone must be provided
      email: yup
        .string()
        .email(t("qtg.proposal.validation.emailInvalid"))
        .test(
          "email-or-phone",
          t("qtg.proposal.validation.emailOrPhoneRequired"),
          function (value) {
            const { clientPhoneNumber } = this.parent;
            // Pass if either email or phone is provided
            return (
              !!(value && value.trim()) ||
              !!(clientPhoneNumber && clientPhoneNumber.trim())
            );
          }
        ),
      clientPhoneNumber: yup
        .string()
        .test(
          "email-or-phone",
          t("qtg.proposal.validation.emailOrPhoneRequired"),
          function (value) {
            const { email } = this.parent;
            // Pass if either phone or email is provided
            return !!(value && value.trim()) || !!(email && email.trim());
          }
        ),
      // Priority 1: Driver licence date validation with birth date
      licenceDate: yup
        .date()
        .nullable()
        .when("clientAge", {
          is: (birthDate: any) => birthDate != null && birthDate !== "",
          then: (schema) =>
            schema
              .max(today, t("qtg.proposal.validation.licenceDateFuture"))
              .test(
                "min-age-15",
                t("qtg.proposal.validation.licenceDateMinAge"),
                function (value) {
                  const { clientAge } = this.parent;
                  if (!clientAge || !value) return true;

                  // Calculate birth date from age (rough approximation)
                  const currentYear = new Date().getFullYear();
                  const birthYear = currentYear - parseInt(clientAge);
                  const birthDate = new Date(birthYear, 0, 1);

                  const minLicenseDate = new Date(birthDate);
                  minLicenseDate.setFullYear(minLicenseDate.getFullYear() + 15);

                  return new Date(value) >= minLicenseDate;
                }
              ),
        }),
    }),
    proposalData: yup.object().shape({
      proposalNumber: yup
        .string()
        .required(t("qtg.proposal.validation.proposalNumberRequired")),
      // Priority 1: Policy start date must be >= today
      policyStartDate: yup
        .date()
        .required(t("qtg.proposal.validation.policyStartDateRequired"))
        .min(today, t("qtg.proposal.validation.policyStartDateFuture")),
      policyEndDate: yup
        .date()
        .required(t("qtg.proposal.validation.policyEndDateRequired")),
    }),
  });
};

export const PROPOSAL_TAB_CONFIG = {
  generalInfo: {
    name: "generalInfo",
    title: "qtg.proposal.tabs.generalInfo.title",
    fields: [
      "vehicleData.licensePlateNumber",
      "vehicleData.vin",
      "vehicleData.vehicleMake",
      "vehicleData.vehicleModel",
      "clientData.firstName",
      "clientData.lastName",
      "clientData.email",
      "clientData.phone",
      "proposalData.proposalNumber",
      "proposalData.policyStartDate",
      "proposalData.policyEndDate",
    ],
  },
  mtplQuotes: {
    name: "mtplQuotes",
    title: "qtg.proposal.tabs.mtplQuotes.title",
    fields: [],
  },
  cascoQuotes: {
    name: "cascoQuotes",
    title: "qtg.proposal.tabs.cascoQuotes.title",
    fields: [],
  },
  documents: {
    name: "documents",
    title: "qtg.proposal.tabs.documents.title",
    fields: [],
  },
  vehicleDetails: {
    name: "vehicleDetails",
    title: "qtg.proposal.tabs.vehicleDetails.title",
    fields: [],
  },
};
